import React from "react";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

// The app's time picker (see Timecard settings): MUI's time picker with the
// violet selection and the rounded, violet-focused field.
//
// `value` and `onChange` speak 24-hour clock strings ("HH:mm"). MUI emits
// intermediate, invalid values while a time is typed; the field keeps those
// as a local draft so typing is not snapped back, and only a complete, valid
// time reaches `onChange`.
const fieldSx = (height, width) => ({
  ...(width ? { width } : {}),
  "& .MuiOutlinedInput-root": { height, backgroundColor: "white", borderRadius: "8px", fontSize: height <= 36 ? "13px" : undefined, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" } },
});
const POPPER_SX = {
  "& .MuiMenuItem-root.Mui-selected": {
    backgroundColor: "#6d28d9",
    color: "white",
    // MUI's hover and focus states would otherwise bring back its default blue.
    "&:hover, &:focus, &.Mui-focusVisible": { backgroundColor: "#5b21b6" },
  },
  "& .MuiPickersLayout-actionBar .MuiButton-root": { color: "#6d28d9" },
};
const parseClock = (time) => (time ? dayjs(`2000-01-01T${time}`) : null);

// `clearable` makes an empty field a value of its own: it reports null, for
// optional times. By default an empty field is an intermediate state.
export default function RightworkTimePicker({ value, onChange, label, clearable = false, disabled = false, height = 36, width = 128 }) {
  const [draft, setDraft] = React.useState(() => parseClock(value));
  const invalid = React.useRef(false);
  React.useEffect(() => { setDraft(parseClock(value)); invalid.current = false; }, [value]);
  // Editing that ends on an incomplete or out-of-range value goes back to
  // the committed one, so the field never shows a value the form is not
  // using while its actions stay enabled.
  const settle = () => {
    if (invalid.current) {
      setDraft(parseClock(value));
      invalid.current = false;
    }
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimePicker
        value={draft}
        disabled={disabled}
        onChange={(picked, context) => {
          setDraft(picked);
          const complete = Boolean(picked && picked.isValid() && !context?.validationError);
          const cleared = clearable && picked === null;
          invalid.current = !complete && !cleared;
          if (complete) onChange(picked.format("HH:mm"));
          if (cleared) onChange(null);
        }}
        onClose={settle}
        slotProps={{ textField: { size: "small", sx: fieldSx(height, width), inputProps: { "aria-label": label }, onBlur: settle }, popper: { sx: POPPER_SX } }}
      />
    </LocalizationProvider>
  );
}
